import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { caseApi, tsfadApi } from '../services/api';
import { waitForHealth } from '../services/waitForHealth';
import {
  Case,
  RemoteTroubleshootingPlan,
  RemoteSuspectGroup,
  RemoteTestStatus
} from '../types';

type PriorityLevel = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
type DomainFilter = 'ALL' | string;

const getCaseDisplayNumber = (record?: Partial<Case> | null): string => record?.caseCode || record?.caseNumber || record?.id || '';
const COMPLETION_STATUSES: RemoteTestStatus[] = ['PASSED', 'FAILED', 'NA', 'IGNORED'];
const ACTIVE_RESOURCE_STATUSES: RemoteTestStatus[] = ['PENDING', 'IN_PROGRESS', 'FAILED'];

const STATUS_STYLES: Record<RemoteTestStatus, { background: string; color: string }> = {
  PENDING: { background: '#f8fafc', color: '#0f172a' },
  IN_PROGRESS: { background: '#e0f2fe', color: '#0369a1' },
  PASSED: { background: '#dcfce7', color: '#166534' },
  FAILED: { background: '#fee2e2', color: '#b91c1c' },
  NA: { background: '#e5e7eb', color: '#4b5563' },
  IGNORED: { background: '#f5f3ff', color: '#6d28d9' }
};

const PRIORITY_BADGE: Record<PriorityLevel, { background: string; color: string }> = {
  CRITICAL: { background: '#fee2e2', color: '#b91c1c' },
  HIGH: { background: '#fef3c7', color: '#92400e' },
  MEDIUM: { background: '#e0f2fe', color: '#0369a1' },
  LOW: { background: '#ede9fe', color: '#5b21b6' }
};

const statusLabels: Record<RemoteTestStatus, string> = {
  PENDING: 'Pending',
  IN_PROGRESS: 'In Progress',
  PASSED: 'Pass',
  FAILED: 'Fail',
  NA: 'N/A',
  IGNORED: 'Ignored'
};

const statusOptions: RemoteTestStatus[] = ['IN_PROGRESS', 'PASSED', 'FAILED', 'NA', 'IGNORED'];

const formatMinutes = (minutes: number): string => {
  if (!minutes || minutes <= 0) return '—';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins === 0 ? `${hours} hr` : `${hours} hr ${mins} min`;
};

const cardBase = {
  background: 'white',
  borderRadius: '1rem',
  padding: '1.25rem',
  boxShadow: '0 12px 32px -16px rgba(15, 23, 42, 0.18)',
  border: '1px solid #e2e8f0'
};

const styles = {
  container: {
    padding: '2rem',
    minHeight: '100vh',
    backgroundColor: '#f8fafc'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '1.5rem'
  },
  titleBlock: {
    maxWidth: '70%'
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: 700,
    marginBottom: '0.5rem',
    color: '#0f172a'
  },
  subtitle: {
    fontSize: '0.95rem',
    color: '#475569',
    lineHeight: 1.5
  },
  backButton: {
    border: '1px solid #cbd5f5',
    background: 'white',
    color: '#1d4ed8',
    padding: '0.5rem 1rem',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    fontWeight: 500
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gap: '2rem'
  },
  section: {
    marginBottom: '1.5rem'
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#0f172a',
    marginBottom: '0.75rem'
  },
  card: cardBase,
  domainGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '1rem'
  },
  domainCard: (selected: boolean) => ({
    ...cardBase,
    border: selected ? '2px solid #2563eb' : '1px solid #e2e8f0',
    cursor: 'pointer',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    transform: selected ? 'translateY(-2px)' : 'none'
  }),
  domainLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#2563eb',
    letterSpacing: '0.02em',
    textTransform: 'uppercase' as const,
    marginBottom: '0.35rem'
  },
  highlightList: {
    marginTop: '0.75rem',
    paddingLeft: '1.1rem',
    color: '#475569',
    fontSize: '0.88rem'
  },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: '#e2e8f0',
    color: '#334155',
    borderRadius: '999px',
    padding: '0.25rem 0.6rem',
    fontSize: '0.75rem',
    fontWeight: 500,
    marginRight: '0.5rem',
    marginBottom: '0.5rem'
  },
  progressBar: {
    height: '12px',
    borderRadius: '999px',
    backgroundColor: '#e2e8f0',
    overflow: 'hidden'
  },
  progressFill: (percent: number) => ({
    width: `${percent}%`,
    height: '100%',
    background: 'linear-gradient(90deg, #2563eb 0%, #1d4ed8 100%)',
    transition: 'width 0.3s ease'
  }),
  suspectGroup: {
    ...cardBase,
    marginBottom: '1rem'
  },
  suspectHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.75rem'
  },
  suspectTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#1e293b'
  },
  priorityBadge: (priority: PriorityLevel) => ({
    background: PRIORITY_BADGE[priority].background,
    color: PRIORITY_BADGE[priority].color,
    borderRadius: '999px',
    padding: '0.25rem 0.75rem',
    fontSize: '0.75rem',
    fontWeight: 600
  }),
  testCard: {
    border: '1px solid #e2e8f0',
    borderRadius: '0.75rem',
    padding: '1rem',
    marginBottom: '0.75rem',
    background: '#ffffff'
  },
  testHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '0.5rem'
  },
  statusChip: (status: RemoteTestStatus) => ({
    background: STATUS_STYLES[status].background,
    color: STATUS_STYLES[status].color,
    padding: '0.25rem 0.75rem',
    borderRadius: '999px',
    fontSize: '0.75rem',
    fontWeight: 600
  }),
  statusButtons: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '0.5rem',
    marginTop: '0.75rem'
  },
  statusButton: (active: boolean, status: RemoteTestStatus) => ({
    background: active ? STATUS_STYLES[status].background : '#f1f5f9',
    color: active ? STATUS_STYLES[status].color : '#334155',
    border: '1px solid #CBD5F5',
    borderRadius: '0.5rem',
    padding: '0.35rem 0.9rem',
    fontSize: '0.8rem',
    fontWeight: 500,
    cursor: 'pointer'
  }),
  resetButton: {
    background: '#f8fafc',
    color: '#475569',
    border: '1px dashed #cbd5f5',
    borderRadius: '0.5rem',
    padding: '0.35rem 0.9rem',
    fontSize: '0.8rem',
    cursor: 'pointer'
  },
  checklistItem: {
    padding: '0.6rem 0.75rem',
    borderRadius: '0.65rem',
    border: '1px dashed #cbd5f5',
    marginBottom: '0.5rem',
    background: '#f8fafc',
    fontSize: '0.85rem'
  },
  warningCard: {
    borderLeft: '4px solid #facc15',
    padding: '0.75rem 1rem',
    borderRadius: '0.75rem',
    background: '#fffbeb',
    marginBottom: '0.75rem',
    color: '#713f12',
    fontSize: '0.85rem'
  },
  noteInput: {
    width: '100%',
    borderRadius: '0.5rem',
    border: '1px solid #CBD5F5',
    padding: '0.5rem 0.75rem',
    fontSize: '0.85rem',
    marginTop: '0.75rem',
    resize: 'vertical' as const,
    minHeight: '60px'
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '2rem',
    background: '#ffffff',
    borderRadius: '1rem',
    border: '1px dashed #cbd5f5',
    color: '#475569'
  }
};

export default function TSFADRemoteFlow() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [planLoading, setPlanLoading] = useState(false);
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [plan, setPlan] = useState<RemoteTroubleshootingPlan | null>(null);
  const [planModelNumber, setPlanModelNumber] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusMap, setStatusMap] = useState<Record<string, RemoteTestStatus>>({});
  const [notesMap, setNotesMap] = useState<Record<string, string>>({});
  const [selectedDomain, setSelectedDomain] = useState<DomainFilter>('ALL');

  const caseIdentifier = useMemo(() => getCaseDisplayNumber(caseData), [caseData]);
  useEffect(() => {
    const initialise = async () => {
      if (!caseId) {
        setError('Missing case identifier.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const caseRecord = await caseApi.getById(caseId);
        setCaseData(caseRecord);

        const modelNumber = caseRecord?.device?.modelNumber;
        if (!modelNumber) {
          setError('Case does not have an associated device model. Remote flow requires documented model assets.');
          return;
        }

        setPlanLoading(true);
        const apiReady = await waitForHealth();
        if (!apiReady) {
          setError('Backend services are not ready. Please try again in a moment.');
          return;
        }

        const planResponse = await tsfadApi.getRemotePlan({
          caseId,
          deviceModel: modelNumber,
          problemDescription: caseRecord.problemDescription,
          symptoms: caseRecord.symptoms || [],
          customerInputs: caseRecord.customerInputs || []
        });

        setPlan(planResponse.remotePlan);
        setPlanModelNumber(planResponse.modelNumber);
        const initialStatuses: Record<string, RemoteTestStatus> = {};
        planResponse.remotePlan.suspectGroups.forEach(group => {
          group.remoteTests.forEach(test => {
            initialStatuses[test.id] = test.status;
          });
        });
        setStatusMap(initialStatuses);
      } catch (err: any) {
        console.error('Failed to load remote plan:', err);
        setError(err?.message || 'Failed to load remote troubleshooting plan.');
      } finally {
        setPlanLoading(false);
        setLoading(false);
      }
    };

    initialise();
  }, [caseId]);

  const handleStatusChange = (testId: string, status: RemoteTestStatus) => {
    setStatusMap(prev => ({
      ...prev,
      [testId]: status
    }));
  };

  const resetStatus = (testId: string) => {
    setStatusMap(prev => ({
      ...prev,
      [testId]: 'PENDING'
    }));
  };

  const handleNoteChange = (testId: string, value: string) => {
    setNotesMap(prev => ({
      ...prev,
      [testId]: value
    }));
  };

  const totalTests = useMemo(() => {
    if (!plan) return 0;
    return plan.suspectGroups.reduce((count, group) => count + group.remoteTests.length, 0);
  }, [plan]);

  const completedTests = useMemo(() => {
    if (!plan) return 0;
    return plan.suspectGroups.reduce((count, group) => {
      return (
        count +
        group.remoteTests.filter(test => COMPLETION_STATUSES.includes(statusMap[test.id] || test.status)).length
      );
    }, 0);
  }, [plan, statusMap]);

  const completionPercent = totalTests === 0 ? 0 : Math.round((completedTests / totalTests) * 100);

  const filteredGroups: RemoteSuspectGroup[] = useMemo(() => {
    if (!plan) return [];
    if (selectedDomain === 'ALL') return plan.suspectGroups;
    return plan.suspectGroups.filter(group => group.domain === selectedDomain);
  }, [plan, selectedDomain]);

  const activeResourceLists = useMemo(() => {
    if (!plan) {
      return {
        tools: [] as string[],
        parts: [] as string[]
      };
    }

    const toolSet = new Set<string>();
    const partSet = new Set<string>();

    plan.suspectGroups.forEach(group => {
      group.remoteTests.forEach(test => {
        const status = statusMap[test.id] || test.status;
        if (!ACTIVE_RESOURCE_STATUSES.includes(status)) return;

        (test.requiredTools || []).forEach(tool => toolSet.add(tool));
        (test.requiredParts || []).forEach(part => partSet.add(part));
      });
    });

    return {
      tools: Array.from(toolSet).sort(),
      parts: Array.from(partSet).sort()
    };
  }, [plan, statusMap]);

  const visitTimeEstimate = useMemo(() => {
    if (!plan) return 0;
    return plan.suspectGroups.reduce((sum, group) => {
      return (
        sum +
        group.remoteTests.reduce((groupSum, test) => {
          return groupSum + (test.estimatedTimeMin || 0);
        }, 0)
      );
    }, 0);
  }, [plan]);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          <div style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem' }}>Loading remote flow...</div>
          <div style={{ color: '#64748b' }}>Compiling case data and domain knowledge assets.</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          <div style={{ fontSize: '1.05rem', fontWeight: 600, marginBottom: '0.75rem' }}>Remote flow unavailable</div>
          <div style={{ color: '#b91c1c', marginBottom: '1rem' }}>{error}</div>
          <button style={styles.backButton} onClick={() => navigate(`/tsfad/${caseId}`)}>
            Return to Troubleshooting From a Distance (TSFAD)
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.titleBlock}>
          <div style={styles.title}>TSFAD Remote Troubleshooting</div>
          <div style={styles.subtitle}>
            {caseIdentifier ? `Case ${caseIdentifier}` : 'Active case'} ·{' '}
            {planModelNumber || caseData?.device?.modelNumber || 'Unknown model'} ·{' '}
            {caseData?.device?.customer?.companyName || caseData?.customer?.companyName || 'Customer unknown'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button style={styles.backButton} onClick={() => navigate(`/tsfad/${caseId}`)}>
            Back to suspect workspace
          </button>
          <button style={styles.backButton} onClick={() => navigate('/tech')}>
            Tech dashboard
          </button>
        </div>
      </div>

      <div style={styles.section}>
        <div style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <div>
              <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: '0.35rem' }}>Problem statement</div>
              <div style={{ color: '#1f2937', fontSize: '0.95rem', maxWidth: '780px' }}>
                {caseData?.problemDescription || plan?.caseContext?.problemDescription || 'No problem description provided.'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', marginBottom: '0.25rem', textTransform: 'uppercase' as const, color: '#64748b' }}>
                Remote plan progress
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, textAlign: 'right', color: '#2563eb' }}>
                {completionPercent}%
              </div>
              <div style={{ fontSize: '0.8rem', color: '#475569', textAlign: 'right' }}>
                {completedTests} of {totalTests} tests reviewed
              </div>
            </div>
          </div>
          {planLoading ? (
            <div style={{ color: '#64748b' }}>Compiling remote plan from domain assets...</div>
          ) : (
            <>
              <div style={styles.progressBar}>
                <div style={styles.progressFill(completionPercent)} />
              </div>
              <div style={{ marginTop: '0.75rem', display: 'flex', gap: '1.5rem', flexWrap: 'wrap' as const }}>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>Estimated remote effort</div>
                  <div style={{ fontWeight: 600, color: '#0f172a' }}>{formatMinutes(visitTimeEstimate)}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>Suspect domains</div>
                  <div style={{ fontWeight: 600, color: '#0f172a' }}>{plan?.totals.suspectGroups || 0}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>Remote tests queued</div>
                  <div style={{ fontWeight: 600, color: '#0f172a' }}>{plan?.totals.remoteTests || totalTests}</div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div style={styles.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
          <div style={styles.sectionTitle}>Domain intelligence</div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <div style={styles.tag}>Select domain to focus group-level suspects</div>
          </div>
        </div>
        <div style={styles.domainGrid}>
          <div
            style={styles.domainCard(selectedDomain === 'ALL')}
            onClick={() => setSelectedDomain('ALL')}
          >
            <div style={styles.domainLabel}>All domains</div>
            <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: '0.35rem' }}>Full remote plan</div>
            <div style={{ fontSize: '0.85rem', color: '#475569' }}>
              View every remote suspect and checklist item. Progress tracking covers the entire plan.
            </div>
          </div>

          {plan?.domainSummaries.map(summary => (
            <div
              key={summary.domain}
              style={styles.domainCard(selectedDomain === summary.domain)}
              onClick={() => setSelectedDomain(summary.domain)}
            >
              <div style={styles.domainLabel}>{summary.domain.replace(/_/g, ' ')}</div>
              <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: '0.35rem' }}>{summary.label}</div>
              <div style={{ fontSize: '0.85rem', color: '#475569' }}>{summary.summary}</div>
              {summary.highlights.length > 0 && (
                <ul style={styles.highlightList}>
                  {summary.highlights.slice(0, 3).map((highlight, idx) => (
                    <li key={idx}>{highlight}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </div>

      <div style={styles.layout}>
        <div>
          <div style={styles.sectionTitle}>Remote suspect plan</div>
          {filteredGroups.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>No remote tests in this domain</div>
              <div style={{ color: '#64748b' }}>
                Select another domain or perform on-site Troubleshooting From a Distance (TSFAD) to expand the suspect list.
              </div>
            </div>
          ) : (
            filteredGroups.map(group => (
              <div key={group.id} style={styles.suspectGroup}>
                <div style={styles.suspectHeader}>
                  <div>
                    <div style={styles.suspectTitle}>{group.title}</div>
                    <div style={{ color: '#64748b', fontSize: '0.85rem' }}>{group.description}</div>
                  </div>
                  <div style={styles.priorityBadge(group.priority)}>{group.priority}</div>
                </div>

                {group.remoteTests.map(test => {
                  const status = statusMap[test.id] || test.status;
                  const noteValue = notesMap[test.id] || '';

                  return (
                    <div key={test.id} style={styles.testCard}>
                      <div style={styles.testHeader}>
                        <div>
                          <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: '0.25rem' }}>{test.title}</div>
                          <div style={{ fontSize: '0.85rem', color: '#475569', lineHeight: 1.5 }}>
                            {test.instructions}
                          </div>
                        </div>
                        <div style={styles.statusChip(status)}>{statusLabels[status]}</div>
                      </div>

                      <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: '1rem', fontSize: '0.8rem', color: '#475569', marginBottom: '0.75rem' }}>
                        <div>
                          <strong>Severity:</strong> {test.severity}
                        </div>
                        <div>
                          <strong>Estimate:</strong> {formatMinutes(test.estimatedTimeMin)}
                        </div>
                        {test.requiresCustomer && (
                          <div>
                            <strong>Customer assist required</strong>
                          </div>
                        )}
                      </div>

                      {(test.requiredTools?.length || 0) > 0 && (
                        <div style={{ marginBottom: '0.5rem', fontSize: '0.8rem', color: '#475569' }}>
                          <strong>Tools:</strong> {test.requiredTools.join(', ')}
                        </div>
                      )}
                      {(test.requiredParts?.length || 0) > 0 && (
                        <div style={{ marginBottom: '0.5rem', fontSize: '0.8rem', color: '#475569' }}>
                          <strong>Parts:</strong> {test.requiredParts.join(', ')}
                        </div>
                      )}
                      {(test.references?.length || 0) > 0 && (
                        <div style={{ marginBottom: '0.5rem', fontSize: '0.8rem', color: '#475569' }}>
                          <strong>References:</strong> {test.references.slice(0, 3).join('; ')}
                        </div>
                      )}

                      <div style={styles.statusButtons}>
                        {statusOptions.map(option => (
                          <button
                            key={option}
                            style={styles.statusButton(status === option, option)}
                            onClick={() => handleStatusChange(test.id, option)}
                          >
                            {statusLabels[option]}
                          </button>
                        ))}
                        <button style={styles.resetButton} onClick={() => resetStatus(test.id)}>
                          Reset
                        </button>
                      </div>

                      <textarea
                        style={styles.noteInput}
                        placeholder="Technician notes, measurements, escalation guidance..."
                        value={noteValue}
                        onChange={event => handleNoteChange(test.id, event.target.value)}
                      />
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div>
          <div style={styles.sectionTitle}>Preparation checklist</div>
          <div style={styles.card}>
            <div style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.5rem', color: '#0f172a' }}>
              Active tools
            </div>
            {activeResourceLists.tools.length === 0 ? (
              <div style={{ color: '#64748b', fontSize: '0.85rem' }}>No tools required for remaining remote tests.</div>
            ) : (
              activeResourceLists.tools.map(tool => (
                <div key={tool} style={styles.checklistItem}>
                  {tool}
                </div>
              ))
            )}
            <div style={{ fontSize: '0.9rem', fontWeight: 600, margin: '1rem 0 0.5rem', color: '#0f172a' }}>
              Active parts / consumables
            </div>
            {activeResourceLists.parts.length === 0 ? (
              <div style={{ color: '#64748b', fontSize: '0.85rem' }}>
                No parts required for remaining remote tests.
              </div>
            ) : (
              activeResourceLists.parts.map(part => (
                <div key={part} style={styles.checklistItem}>
                  {part}
                </div>
              ))
            )}
          </div>

          {plan?.warnings.length ? (
            <div style={{ marginTop: '1.5rem' }}>
              <div style={styles.sectionTitle}>Risk highlights</div>
              {plan.warnings.slice(0, 5).map((warning, index) => (
                <div key={`${warning.domain}-${index}`} style={styles.warningCard}>
                  <strong>{warning.domain.replace(/_/g, ' ')}:</strong> {warning.text}
                </div>
              ))}
            </div>
          ) : null}

          {plan?.references.length ? (
            <div style={{ marginTop: '1.5rem' }}>
              <div style={styles.sectionTitle}>Reference links</div>
              <div style={styles.card}>
                <ul style={{ paddingLeft: '1.1rem', color: '#475569', fontSize: '0.85rem', margin: 0 }}>
                  {plan.references.slice(0, 8).map((ref, idx) => (
                    <li key={idx} style={{ marginBottom: '0.5rem' }}>
                      {ref}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
